import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import SingleNewsCard from './components/SingleNewsCard/SingleNewsCard';
import Pagination from '../../components/Pagination/Pagination';
import NewsBanner from './components/NewsBanner/NewsBanner';
import Loader from '../../components/Loader/Loader';
import AIpopup from '../../components/AIPopup/AIpopup';
import { useGetAllNewsQuery, useGetNewsPageFieldsQuery } from './api/NewsApiApiSlice';

import { ReactComponent as NewsArrow } from '../../assets/images/NewsArrow.svg';
import { ReactComponent as NewsFilterIcon } from '../../assets/images/NewsFilterIcon.svg';
import { ReactComponent as NewsSearch } from '../../assets/images/NewsSearch.svg';
import { ReactComponent as NewsAiIcon } from '../../assets/images/newsAiIcon.svg';
import styles from './styles/NewsPage.module.scss';

function News() {
  const loc = useLocation();
  const queryParams = new URLSearchParams(loc.search);
  const pageSize = 6;

  // All useState hooks at the top
  const [page, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [keyword, setKeyword] = useState('');
  const [activeTab, setActiveTab] = useState('featured');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [isAiPopupOpen, setIsAiPopupOpen] = useState(false);

  // All useRef hooks
  const dropdownRef = useRef(null);

  // All API query hooks
  const { data, error, isLoading, isSuccess } = useGetAllNewsQuery({
    page,
    pageSize,
  });

  const { data: latestNews, error: latestNewsError, isLoading: latestNewsIsloading, isSuccess: latestNewsIsSiccess } = useGetAllNewsQuery({
    count: 1,
  })

  const { data: searchData, error: searchError, isLoading: isSearchLoading, isSuccess: isSearchSuccess } = useGetAllNewsQuery({
    page,
    pageSize,
    keyword,
    filter: selectedFilter
  });

  const { data: pageData, error: pageError, isLoading: pageLoading, isSuccess: pageIsSuccess } = useGetNewsPageFieldsQuery();

  const {
    data: filterData,
    error: filterError,
    isLoading: filterIsLoading,
    isSuccess: filterIsSuccess,
  } = useGetAllNewsQuery({
    filter: activeTab,
    count: 3,
  });

  // All useEffect hooks
  useEffect(() => {
    const pageParam = queryParams.get('page');
    setPageNumber(pageParam ? parseInt(pageParam, 10) : 1);
  }, [loc]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (keyword && searchData?.count) {
      setTotalPages(Math.ceil(searchData.count / pageSize));
    } else if (data?.count) {
      setTotalPages(Math.ceil(data.count / pageSize));
    }
  }, [data, searchData, keyword]);

  // Function definitions
  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleFilterSelect = (filter) => {
    setSelectedFilter(filter);
    setIsDropdownOpen(false);
  };

  // Handle loading and error states AFTER all hooks
  if (isLoading || pageLoading || isSearchLoading) return <div>Loading...</div>;
  if (error) return <div>Error loading news: {error.message}</div>;
  if (searchError) return <div>Error loading search results: {searchError.message}</div>;
  if (pageError) return <div>Error loading page data: {pageError.message}</div>;
  if (!data?.results || data.results.length === 0) return <div>No news available</div>;

  // Get featured news
  const featuredNews = filterData?.results?.find((item) => item.is_featured) || filterData?.results?.[0];

  // Use searchData.results if keyword exists, else use data.results
  const newsItems =
    (keyword || selectedFilter) && isSearchSuccess
      ? searchData.results
      : data.results;


  function getPostedTimeDuration(publishDate) {
    const now = new Date();
    const publish = new Date(publishDate);
    const diffInMs = now - publish;

    const diffInSeconds = Math.floor(diffInMs / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);
    const diffInWeeks = Math.floor(diffInDays / 7);
    const diffInMonths = Math.floor(diffInDays / 30); // Approximation
    const diffInYears = Math.floor(diffInDays / 365); // Approximation

    if (diffInYears > 0) {
      return `Posted ${diffInYears} year${diffInYears > 1 ? 's' : ''} ago`;
    } else if (diffInMonths > 0) {
      return `Posted ${diffInMonths} month${diffInMonths > 1 ? 's' : ''} ago`;
    } else if (diffInWeeks > 0) {
      return `Posted ${diffInWeeks} week${diffInWeeks > 1 ? 's' : ''} ago`;
    } else if (diffInDays > 0) {
      return `Posted ${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    } else if (diffInHours > 0) {
      return `Posted ${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    } else if (diffInMinutes > 0) {
      return `Posted ${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
    }
    return 'Posted recently';
  }

  return (
    <>
      {pageIsSuccess && pageData ? (
        <NewsBanner
          title={pageData.bannerTitle || 'News'}
          subtitle={pageData.bannerSubtitle || 'This is the news page.'}
          image={pageData.bannerImage || ''}
          count={keyword && isSearchSuccess ? searchData.news_count_posted_last_week : data.news_count_posted_last_week}
        />
      ) : (
        <div>Loading banner...</div>
      )}

      <section
        className={`${styles.insights} container`}
        style={{
          "--ai-color": pageData?.colors?.aiColor,
          "--primary-color": pageData?.colors?.primary,
          "--secondary-color": pageData?.colors.secondary,
          "--teritiary-color": pageData?.colors.tertiary,
          "--card-buttons": pageData?.colors.cardAndButtons,
          "--insights-title" : pageData?.colors?.insightsTitle,
          "--ai-button-text": pageData?.colors?.aiButtonTextColor,
          "--icon-gradient-1": pageData?.colors.aiIconColors[0],
          "--icon-gradient-2": pageData?.colors.aiIconColors[1],
          "--icon-gradient-3": pageData?.colors.aiIconColors[2],
          "--icon-gradient-4": pageData?.colors.aiIconColors[3] || "#D61663",
          '--card-border-color': pageData.colors.border
        }}
      >
        <div className={styles.insightsContent}>
          {/* Featured card - now using latestNews */}
          <div className={styles.featureCardWrapper}>
            {latestNews?.results?.length > 0 && (
              <>
                <div className={styles.featureHaderWrapper}>
                  <h2
                    style={{
                      color: pageData.colors.primary
                    }}
                  >
                    Explore our latest insights
                  </h2>
                </div>
                <div className={styles.featureCard}>
                  <div className={styles.featuredImage}>
                    <img
                      src={latestNews.results[0].featured_image?.file}
                      alt={latestNews.results[0].featured_image?.alternative_text}
                    />
                    <span className={styles.tag}>
                      {getPostedTimeDuration(latestNews.results[0].publish_date)}
                    </span>
                    <a href={`/news/${latestNews.results[0].slug}`}>
                      <span className={styles.expandIcon}>
                        <NewsArrow />
                      </span>
                    </a>
                  </div>
                  <div className={styles.featuredBody}>
                    <h3>{latestNews.results[0].title}</h3>
                    <p>{latestNews.results[0].excerpt.substring(0, 100)}</p>
                    <div className={styles.actions}>
                      <div
                        className={styles.readMoreRedirect}
                        style={{ color: pageData.colors.secondary }}
                      >
                        <a href={`/news/${latestNews.results[0].slug}`}>
                          Read more
                        </a>
                        ↗
                      </div>
                      <button
                        className={styles.aiBtn}
                        onClick={() => setIsAiPopupOpen(true)}
                      ><NewsAiIcon />Ai summary
                      </button>
                    </div>
                  </div>
                </div>

                {isAiPopupOpen && (
                  <AIpopup
                    popupOpen={isAiPopupOpen}
                    setPopupStatus={setIsAiPopupOpen}
                    data={latestNews.results[0].ai_summary}
                    newsTitle={latestNews.results[0].title}
                  />
                )}
              </>
            )}
          </div>

          {/* Side cards */}
          <div className={styles.sideCardWrapper}>
            <div className={styles.sideHaderWrapper}>
              <div className={styles.tabs}>
                <button
                  className={`${styles.tab} ${activeTab === 'featured' ? styles.active : ''}`}
                  onClick={() => setActiveTab('featured')}
                  style={{
                    backgroundColor: activeTab === 'featured'
                      ? pageData.colors.secondary
                      : pageData.colors.teritiary
                  }}
                >
                  Featured
                </button>

                <button
                  className={`${styles.tab} ${activeTab === 'trending' ? styles.active : ''}`}
                  onClick={() => setActiveTab('trending')}
                  style={{
                    backgroundColor: activeTab === 'trending'
                      ? pageData.colors.secondary
                      : pageData.colors.teritiary
                  }}
                >
                  Trending
                </button>

                <button
                  className={`${styles.tab} ${activeTab === 'latest' ? styles.active : ''}`}
                  onClick={() => setActiveTab('latest')}
                  style={{
                    backgroundColor: activeTab === 'latest'
                      ? pageData.colors.secondary
                      : pageData.colors.teritiary
                  }}
                >
                  Latest
                </button>
              </div>
            </div>
            <div className={styles.sideCards}>
              {filterIsLoading && <Loader />}
              {filterError && <div>Error loading {activeTab} news</div>}
              {filterIsSuccess ? (
                filterData?.results?.length > 0 ? (
                  filterData.results.map((newsItem) => (
                    <a href={`/news/${newsItem.slug}`}>
                      <div
                        key={newsItem.id}
                        className={styles.card}
                      >
                        <img
                          src={newsItem.featured_image?.file}
                          alt={newsItem.featured_image?.alternative_text || "News image"}
                        />
                        <div className={styles.cardBody}>
                          <span className={styles.date}>
                            {new Date(newsItem.publish_date).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            })}
                          </span>
                          <p>{newsItem.excerpt}</p>
                        </div>
                      </div>
                    </a>
                  ))
                ) : (
                  <p className={styles.nullMessage}>No posts to show</p>
                )
              ) : null}
            </div>
          </div>
        </div>
      </section>
      <section className="container">
        <div className={styles.latestPosts}>
          <div className={styles.header}>
            <h2>Latest Posts</h2>
            <div className={styles.controls}>
              <div className={styles.filterContainer} ref={dropdownRef}>
                <button
                  className={styles.filterBtn}
                  onClick={toggleDropdown}
                  style={{
                    backgroundColor: pageData.colors.cardAndButtons
                  }}
                >
                  <NewsFilterIcon />
                  {selectedFilter
                    ? selectedFilter.charAt(0).toUpperCase() + selectedFilter.slice(1)
                    : 'Filter'}
                </button>
                {isDropdownOpen && (
                  <div className={styles.dropdown}>
                    <div
                      className={`${styles.dropdownItem} ${selectedFilter === 'featured' ? styles.selected : ''}`}
                      onClick={() => handleFilterSelect('featured')}
                    >
                      Featured
                    </div>
                    <div
                      className={`${styles.dropdownItem} ${selectedFilter === 'trending' ? styles.selected : ''}`}
                      onClick={() => handleFilterSelect('trending')}
                    >
                      Trending
                    </div>
                    <div
                      className={`${styles.dropdownItem} ${selectedFilter === 'latest' ? styles.selected : ''}`}
                      onClick={() => handleFilterSelect('latest')}
                    >
                      Latest
                    </div>
                    <div
                      className={`${styles.dropdownItem} ${selectedFilter === null ? styles.selected : ''}`}
                      onClick={() => handleFilterSelect(null)}
                    >
                      Clear filter
                    </div>
                  </div>
                )}
              </div>
              <div
                className={styles.searchBox}
                style={{
                  backgroundColor: pageData.colors.cardAndButtons
                }}
              >
                <NewsSearch />
                <input
                  type="text"
                  placeholder="Search posts"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className={styles.postsGrid}>
            {newsItems.length > 0 ? (
              newsItems.map((newsItem) => (
                <SingleNewsCard
                  news={newsItem}
                  colors={pageData.colors}
                  popupOpen={isAiPopupOpen}
                  setPopupStatus={setIsAiPopupOpen}
                />
              ))
            ) : (
              <div>No search results found</div>
            )}
          </div>
          <div className={styles.searchBtn}>
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              pageSize={pageSize}
              onPageChange={setPageNumber}
              listingPage='News'
              colors={pageData.colors}
            />
          </div>
        </div>
      </section>
    </>
  );
}

export default News;